import { AmendmentSchemaResponse, API_ENDPOINTS } from '@/constants';
import { SchemaField } from '@/contexts/ApplicationContext';
import {
  AuthorizedUserResponse,
  createFTSAccountApiPayload,
  SendAccountHolderSmsRequest,
  UpdateUserPayload,
  UpdateUserResponse,
} from '@/types/api/apiTypes';
import { Applicant } from '@/types/applicants/applicantTypes';
import { AmendFTSJobPayload } from '@/types/forms/amendmentFormDefaultValues';
import { Language } from '@/types/forms/newApplicationFormTypes';
import axios, { isAxiosError } from 'axios';
import apiClient from './apiClient';
import unauthenticatedApiClient from './unauthenticatedApiClient';

export const createFTSAccount = async (
  payload: createFTSAccountApiPayload
): Promise<any> => {
  try {
    const response = await unauthenticatedApiClient.post(
      API_ENDPOINTS.CREATE_FTS_ACCOUNT,
      JSON.stringify(payload)
    );
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw (
        error.response.data?.message ||
        error.response.data?.title ||
        error.response.data ||
        'An error occurred.'
      );
    }
    throw new Error('An error occurred. Please try again.');
  }
};

export const verifyFTSAccountSms = async (
  mobile: string,
  email: string
): Promise<any> => {
  const payload = {
    Email: email,
    PhoneNumber: mobile,
  };
  try {
    const response = await unauthenticatedApiClient.post(
      API_ENDPOINTS.VERIFY_FTS_ACCOUNT_SMS,
      JSON.stringify(payload)
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const verifyFTSAccountEmail = async (): Promise<any> => {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.SEND_EMAIL_VERIFICATION_CODE
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const processNewFTSApplicantSubmission = async (
  payload: any
): Promise<any> => {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.PROCESS_NEW_APPLICANT_SUBMISSION,
      JSON.stringify(payload)
    );

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const errorData = error.response?.data;

      // If the backend returned a 400 with a valid JSON body, re-throw it directly
      // This allows to access specific fields like FTSApplicantEligibilityResponseDescription
      if (errorData && typeof errorData === 'object') {
        throw errorData;
      }

      // Otherwise, throw a generic error message
      throw new Error('An unexpected error occurred.');
    }

    // If the error isn't an Axios error, throw a fallback system error
    throw new Error('An unexpected error occurred. Please try again later.');
  }
};

export const processExistingFTSApplicantSubmission = async (
  payload: any
): Promise<any> => {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.PROCESS_EXISTING_APPLICANT_SUBMISSION,
      JSON.stringify(payload)
    );

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const errorData = error.response?.data;

      // If the backend returned a 400 with a valid JSON body, re-throw it directly
      // This allows to access specific fields like FTSApplicantEligibilityResponseDescription
      if (errorData && typeof errorData === 'object') {
        throw errorData;
      }

      // Otherwise, throw a generic error message
      throw new Error('An unexpected error occurred.');
    }

    // If the error isn't an Axios error, throw a fallback system error
    throw new Error('An unexpected error occurred. Please try again later.');
  }
};

export const GetJobsForMyFtsApplications = async (): Promise<any> => {
  try {
    const response = await apiClient.get(
      API_ENDPOINTS.GET_JOBS_FOR_FTS_DASHBOARD
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching applicant data:', error);
    throw error;
  }
};

export const uploadFile = async (
  file: File,
  fileUploadType: number
): Promise<any> => {
  const formData = new FormData();
  formData.append('files', file);
  formData.append('FileUploadType', fileUploadType.toString());

  try {
    const response = await apiClient.post(API_ENDPOINTS.UPLOAD_FILE, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const validationMessage =
        error.response?.data?.FileValidation?.[0] ??
        error.response?.data?.StatusMessage ??
        'Upload failed. Please try again.';

      throw new Error(validationMessage);
    }

    throw new Error('Upload failed. Please try again.');
  }
};

export const getDocumentValidityId = async (data: {
  DocumentType: string;
  Language: string;
  DocumentGUID: string[];
}) => {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.CHECK_DOCUMENT_VALIDITY,
      data
    );

    return response.data;
  } catch (error) {
    return {};
  }
};

export const pollDocumentValidity = async (validityCheckId: string) => {
  try {
    const response = await apiClient.get(API_ENDPOINTS.POLL_DOCUMENT_VALIDITY, {
      params: { validityCheckId },
    });

    return response.data;
  } catch (error) {
    return {};
  }
};

export const processFTSApplicationSubmission = async (data: any) => {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.PROCESS_FTS_APPLICATION_SUBMISSION,
      data
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export const getSpecificFtsApplicationFormData = async (
  ftsApplicantId: string
): Promise<any> => {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.GET_SPECIFIC_APPLICANT_DATA_FOR_PREFILL,
      {
        FTSApplicantId: parseInt(ftsApplicantId, 10),
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching details data:', error);
    throw error;
  }
};

export const FTSJobListingGetJobsForMyFtsApplications = async (): Promise<
  Applicant[]
> => {
  try {
    const response = await apiClient.get(
      API_ENDPOINTS.GET_JOBS_FOR_MY_FTS_APPLICATIONS
    );
    if (response.status == 204) {
      return [];
    }
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('error:', error.response?.data);
      throw new Error(
        error.response?.data?.StatusMessage || 'Failed to retrieve Job Listings'
      );
    } else {
      throw new Error('Failed to retrieve Job Listings');
    }
  }
};

export const getLatestBookingDataSchema = async (): Promise<
  Record<string, any>
> => {
  try {
    const response = await apiClient.get(
      API_ENDPOINTS.GET_LATEST_BOOKING_SCHEMA
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('Error fetching schema:', error.response?.data);
      throw new Error(
        error.response?.data?.StatusMessage || 'Failed to retrieve schema'
      );
    } else {
      throw new Error('Failed to retrieve schema');
    }
  }
};

export const getBookingDataSchemaForJob = async (
  jobId: string
): Promise<SchemaField[]> => {
  try {
    const response = await apiClient.get(
      API_ENDPOINTS.GET_BOOKING_SCHEMA_FOR_JOB(jobId)
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('Error fetching schema:', error.response?.data);
      throw new Error(
        error.response?.data?.StatusMessage || 'Failed to retrieve schema'
      );
    } else {
      throw new Error('Failed to retrieve schema');
    }
  }
};

export const getAmendmentRequestTypeSchema = async (
  jobId: string
): Promise<AmendmentSchemaResponse> => {
  try {
    const response = await apiClient.get(
      API_ENDPOINTS.GET_AMENDMENT_REQUEST_TYPE_SCHEMA(jobId)
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('Error fetching schema:', error.response?.data);
      throw new Error(
        error.response?.data?.StatusMessage || 'Failed to retrieve schema'
      );
    } else {
      throw new Error('Failed to retrieve schema');
    }
  }
};

export const getLanguagesAvailableForTranslation = async (): Promise<
  Language[]
> => {
  try {
    const response = await apiClient.get(API_ENDPOINTS.GET_LANGUAGES_AVAILABLE);

    return response.data;
  } catch (error) {
    console.error('Error fetching applicant data:', error);
    throw error;
  }
};

export const getFTSBookingDocumentTypes = async (): Promise<any> => {
  try {
    const response = await apiClient.get(
      API_ENDPOINTS.GET_BOOKING_DOCUMENT_TYPES
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching applicant data:', error);
    throw error;
  }
};

export const getDownloadDocumentUrl = async (
  documentGuid: string
): Promise<string> => {
  try {
    const response = await apiClient.get(
      API_ENDPOINTS.DOWNLOAD_DOCUMENT(documentGuid)
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching download URL for ${documentGuid}:`, error);
    throw error;
  }
};

export const getFTSJobDetails = async (ftsJobId: string): Promise<any> => {
  try {
    const response = await apiClient.get(
      API_ENDPOINTS.GET_JOB_DETAILS(ftsJobId)
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching details data:', error);
    throw error;
  }
};

export const getFTSJobHistory = async (ftsJobId: string): Promise<any> => {
  try {
    const response = await apiClient.get(
      API_ENDPOINTS.GET_JOB_HISTORY(ftsJobId)
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching details data:', error);
    throw error;
  }
};

// using this endpoint temporarily - TBC whether updating the user's mobile will require an sms or email verification
export const sendPhoneVerification = async (
  payload: SendAccountHolderSmsRequest
): Promise<any> => {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.SEND_SMS_FOR_UPDATE,
      JSON.stringify(payload)
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

// Retrieve currently logged in user's account details
export const FTSAccountHolderGetAuthenticatedFTSAccountHolder =
  async (): Promise<AuthorizedUserResponse> => {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.GET_AUTHENTICATED_ACCOUNT_HOLDER
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('error:', error.response?.data);
        throw new Error(
          error.response?.data?.StatusMessage || 'Failed to retrieve User info'
        );
      } else {
        throw new Error('Failed to retrieve User info');
      }
    }
  };

// Update logged in user's account details
export const FTSAccountHolderUpdateFTSAccountHolder = async (
  payload: UpdateUserPayload
): Promise<UpdateUserResponse> => {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.UPDATE_ACCOUNT_HOLDER,
      JSON.stringify(payload)
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getFinalTranslationDocumentUrl = async (
  jobId: number
): Promise<any> => {
  try {
    const response = await apiClient.get(
      API_ENDPOINTS.GET_FINAL_TRANSLATION_DOC_URL(jobId)
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching final translation document URL', error);
    throw error;
  }
};

export const getCorrectionRequestReasons = async (): Promise<any> => {
  try {
    const response = await apiClient.get(
      API_ENDPOINTS.GET_CORRECTION_REQUEST_REASONS
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Submits a correction request for a specific job.
 * @param {number} jobId The Job ID for which the correction request is being submitted
 * @param {string} correctionRequestReasonCodes The correction request reason code, or a list of codes delimited by semi-colons.
 * @param {string} additionalInstructions The instructions for the translator.
 * @returns {Promise<any>} A promise that resolves to the response from the API.
 * @throws {Error} If the API request fails, an error is thrown with a message indicating the failure reason.
 */
export const submitCorrectionRequest = async (
  jobId: number,
  correctionRequestReasonCodes: string,
  additionalInstructions: string
): Promise<any> => {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.SUBMIT_CORRECTION_REQUEST,
      {
        FTSJobId: jobId,
        CorrectionRequestType: correctionRequestReasonCodes,
        CorrectionInstructions: additionalInstructions,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const withdrawCorrectionRequest = async (
  jobId: number
): Promise<any> => {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.WITHDRAW_CORRECTION_REQUEST,
      {
        FTSJobId: jobId,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Amendments
export const submitAmendment = async (
  payload: AmendFTSJobPayload
): Promise<any> => {
  try {
    const response = await apiClient.post(API_ENDPOINTS.AMEND_FTS_JOB, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCountries = async (): Promise<any> => {
  try {
    const response = await apiClient.get(API_ENDPOINTS.GET_COUNTRIES);

    return response.data;
  } catch (error) {
    console.error('Error fetching countries:', error);
    throw error;
  }
};
